import { useEffect, useMemo, useRef, useState } from 'react';
import InlineText from '../../InlineEdit/InlineText';
import './MenuToc.scss';

const View = (props: any) => {
  const { data, isEditMode, block, onChangeBlock } = props;
  // Memoised so the `|| []` fallback keeps a stable identity across renders,
  // which the IntersectionObserver effect below depends on.
  const links: any[] = useMemo(() => data.links || [], [data.links]);
  const [activeAnchor, setActiveAnchor] = useState<string>('');
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isEditMode || links.length === 0) return;

    const anchors = links.map((link) => link.anchor).filter(Boolean);
    const elements = anchors
      .map((anchor: string) => document.getElementById(anchor))
      .filter(Boolean) as HTMLElement[];

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveAnchor(visible[0].target.id);
        }
      },
      { rootMargin: '-128px 0px -50% 0px', threshold: 0 },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [links, isEditMode]);

  const updateLink = (
    index: number,
    patch: Partial<{ label: string; anchor: string }>,
  ) => {
    const next = links.map((l, i) => (i === index ? { ...l, ...patch } : l));
    onChangeBlock(block, { ...data, links: next });
  };

  const handleAdd = () => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
    const next = [...links, { '@id': id, label: '', anchor: '' }];
    onChangeBlock(block, { ...data, links: next });
  };

  const handleRemove = (index: number) => {
    const next = links.filter((_, i) => i !== index);
    onChangeBlock(block, { ...data, links: next });
  };

  if (!isEditMode && links.length === 0) return null;

  return (
    <nav className="menu-toc" ref={navRef}>
      <div className="menu-toc-inner">
        {links.map((link, i) => (
          <span
            key={link['@id'] ?? i}
            className={`menu-toc-item inline-host${activeAnchor === link.anchor ? ' is-active' : ''}`}
          >
            {isEditMode ? (
              <>
                <InlineText
                  value={link.label || ''}
                  onChange={(label) => updateLink(i, { label })}
                  renderTag="span"
                  renderClassName="menu-toc-label"
                  placeholder="Libellé"
                  ariaLabel={`Libellé du lien ${i + 1}`}
                />
                <button
                  type="button"
                  aria-label={`Supprimer le lien ${i + 1}`}
                  className="inline-remove"
                  onClick={() => handleRemove(i)}
                >
                  ✕
                </button>
              </>
            ) : (
              <a
                href={`#${link.anchor}`}
                onClick={(e) => {
                  e.preventDefault();
                  const target = document.getElementById(link.anchor);
                  if (target) target.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {link.label}
              </a>
            )}
          </span>
        ))}
        {isEditMode && (
          <button
            type="button"
            className="inline-add menu-toc-add"
            onClick={handleAdd}
          >
            + Ajouter un lien
          </button>
        )}
      </div>
    </nav>
  );
};

export default View;
